import React, { useRef, useState, useEffect } from 'react';
import lodash from 'lodash';
import { connect } from 'react-redux';
import moment from 'moment';
import styles from 'pages/studyOverview/containers/StudyContainer/StudyContainer.scss';
import OverviewWidget from './OverviewWidget';
import DaysInLifecycle from './DaysInLifecycle';
import { getPendingStudyTasksCount } from 'pages/studyOverview/actions/tasks.js';
import { PageTitle } from 'components/common/Study/PageComponents';
import {
    evaluateStudyExpectations,
    unloadStudyExpectations,
} from '../../actions/studyExpectations';
import { UserCanViewStudyExpectations } from 'common/permissions/components/actionButton';
import {binderEmailDomainSelector} from 'common/selectors';
import { Conditional } from 'common/components/layout';
import { manageStudyTasksPredicate, uploadDocumentPredicate } from 'common/permissions/studyPredicates';
import { unfiledDocumentsPredicate } from 'common/permissions/predicates';
import { loadHomePageV4Access } from '../../../../common/features/predicates';

export function StudyOverview(props) {

    const [showCopySuccess, setShowCopySuccess] = useState(false);
    const emailCopy = useRef(null);
    const currentStage = lodash.get(props, 'studyStage', null);
    const stageProgressions = lodash.get(props, 'studyStageProgressions', []);
    const unfiledDocumentsCount = lodash.get(props, 'binder.countUnfiledDocuments', 0);
    const pendingTasksCount = lodash.get(props, 'study.pendingTasksCount', 0);
    const binder = lodash.get(props, 'binder',{});
    const SecuredMissingDocumentTile = UserCanViewStudyExpectations(OverviewWidget);
    const binderForwardingAddress = getBinderForwardingAddress(binder, props.binderEmailDomain);
    const canManageStudyTasks = lodash.get(props, 'canManageStudyTasks');
    const canUploadDocument = lodash.get(props, 'canUploadDocument');
    const canSeeUnfiledDocuments = lodash.get(props, 'canSeeUnfiledDocuments');
    
    useEffect(() => {
        if (!showCopySuccess) return;

        const timer = setTimeout(() => setShowCopySuccess(false), 3000);
        return () => clearTimeout(timer);
    }, [showCopySuccess]);

    useEffect(() => {
        props.evaluateStudyExpectations();
        props.getPendingStudyTasksCount();
        return () => {
            props.unloadStudyExpectations();
        };
    }, []);

    const copyToClipboard = (e) => {
        emailCopy.current.select();

        document.execCommand('copy');
        e.target.focus();

        setShowCopySuccess(true);
    };

    const isStudyExpectationsDataError = () => {
        return !props.isLoadingExpectedDocuments && lodash.isNull(props.missingStaffCount) && lodash.isNull(props.missingStudyCount);
    };

    return (
        <div className={styles.OverviewSection} data-cy="StudyOverview">
            <div className={styles.Page_Banner_Text}>
                <PageTitle>Dashboard</PageTitle>
                <Conditional condition={canUploadDocument}>
                    <div className={styles.emailSection}>
                        <Conditional condition={showCopySuccess}>
                            <div className={styles.successMessage}>Study Forwarding Address copied</div>
                        </Conditional>                                      
                        <span className={styles.copy} onClick={copyToClipboard} data-cy="StudyOverview-copyToClipboard">
                        Copy {props.complion4FlagEnabled ? 'Binder' : 'Study'} Forwarding Address
                        </span>
                        <input
                            className={styles.hidden}
                            ref={emailCopy}
                            defaultValue={binderForwardingAddress.value.address}
                            data-cy="StudyOverview-input">
                        </input>
                    </div>
                </Conditional>

            </div>

            <ExpiringMessage study={props.study} complion4FlagEnabled={props.complion4FlagEnabled}/>
            <DaysInLifecycle
                currentStage={currentStage}
                stageProgressions={stageProgressions} />
            <div className={styles.widgetContainer} data-cy="StudyOverview-widgetContainer">

                <SecuredMissingDocumentTile
                    label={'Missing Documents'}
                    text={'Number of study and staff documents that are currently missing.'}
                    widgetPath={`/study/${props.study.id}/expectations/missing-documents`}
                    errorText={'We can\'t show data because there is either an error or you haven\'t setup expectations.'}
                    primaryCount={props.missingStudyCount}
                    secondaryCount={props.missingStaffCount}
                    primaryLabel={props.complion4FlagEnabled ? 'Binder' : 'Study'}
                    secondaryLabel={'Staff'}
                    isError={isStudyExpectationsDataError()}
                    isLoadingExpectedDocuments={props.isLoadingExpectedDocuments}
                    isPrimaryAlert
                    isSecondaryAlert                    
                />

                <Conditional condition={canManageStudyTasks} >
                    <OverviewWidget
                        label={'Pending Tasks'}
                        text={'Number of actions that are currently pending on Documents.'}
                        widgetPath={`/study/${props.study.id}/tasks`}
                        primaryCount={pendingTasksCount}
                        isPrimaryAlert />
                </Conditional>

                <Conditional condition={canSeeUnfiledDocuments}>
                    <OverviewWidget
                        label={'Unfiled Documents'}
                        text={'Number of documents that are currently unfiled.'}
                        widgetPath={`/study/${props.study.id}/documents?folder=unfiled`}
                        primaryCount={unfiledDocumentsCount}
                        isPrimaryAlert/>
                </Conditional>
            </div>
        </div>
    );
}


function ExpiringMessage({ study, complion4FlagEnabled }) {
    const creationDate = moment(study.createdAt);
    const currentDate = moment();
    const welcomeMessageExpirationDate = creationDate.add(24, 'hours');

    //if 24 hours has passed after creation display the default text
    if (currentDate.isAfter(welcomeMessageExpirationDate)) {
        return (<div data-cy="StudyOverview-isAfter">
            <p className={styles.timeout_caption}>This should give you a quick snapshot of the status of your {complion4FlagEnabled ? 'binder' : 'study'}. We've identified some areas for you to potentially look into.</p>
        </div>);
    }

    //return original text
    return (
        <div className={styles.blue_box} data-cy="StudyOverview-original">
            <h4 className={styles.blue_header}>Your {complion4FlagEnabled ? 'Binder' : 'Study'} Has Been Created!</h4>
            <p className={styles.caption}>Here are a few things you can do to get started:</p>
            <ul data-cy="StudyOverview-originalUl">
                <li className={styles.list_item}>- Check your auto-filling results in the Documents tab </li>
                <li className={styles.list_item}>- Send documents for signature or review, and check their status under the tasks tab</li>
                <li className={styles.list_item}>- Change your {complion4FlagEnabled ? 'binder' : 'study'} settings as needed under the Settings tab</li>
                <li className={styles.list_item}>- Forward the correspondence and attachments into this {complion4FlagEnabled ? 'binder' : 'study'} by using the address:</li>
            </ul>
        </div>

    );
}

function getBinderForwardingAddress(binder,binderEmailDomain){
    if(binder){
        return {
            label: 'Binder Forwarding Address',
            value: {
                address: `${binder.id}@${binderEmailDomain}`,
            },
        };
    }
    else{
        return{
            label: 'Binder Forwarding Address',
            value:{
                address:'Binder Does Not Exist'
            },
        };
    }
}

export default connect(state => {
    const missingStudyCount = lodash.get(state, 'studyExpectations.missingStudyCount', null);
    const missingStaffCount = lodash.get(state, 'studyExpectations.missingStaffCount', null);
    const isLoadingExpectedDocuments = lodash.get(state, 'studyExpectations.isLoading', false);
    const binderEmailDomain = binderEmailDomainSelector(state);
    const canManageStudyTasks = manageStudyTasksPredicate(state);
    const canUploadDocument = uploadDocumentPredicate(state);
    const binderPermissions = lodash.get(state, 'me.binderPermissions', {});
    const canSeeUnfiledDocuments = unfiledDocumentsPredicate(binderPermissions);
    return {
        missingStudyCount,
        missingStaffCount,
        isLoadingExpectedDocuments,
        binderEmailDomain,
        canManageStudyTasks,
        canUploadDocument,
        canSeeUnfiledDocuments,
        complion4FlagEnabled: loadHomePageV4Access(state),
    };
}, {
    evaluateStudyExpectations,
    unloadStudyExpectations,
    getPendingStudyTasksCount,
})(StudyOverview);